import React, { useState } from 'react';
import { t } from '../tokens';

interface GigToolbarProps {
  transposeAmount: number;
  gigChordScale: number;
  onTranspose: (delta: number) => void;
  onLyricsSizeChange: (delta: number) => void;
  onChordScaleChange: (delta: number) => void;
}

// Proportional left/width derived from Figma 402px reference frame (#312:299)
const p = (px: number) => `${(px / 402 * 100).toFixed(3)}%`;

const FRAME_W = 402; // Figma reference width — controls block never exceeds it

// When closed: show top 20px (rounded corners + divider visible as tap affordance)
const CONTENT_H = 82; // Figma #348:55 height
const CLOSED_OFFSET = CONTENT_H - 20; // 62px → 20px peek

const GigToolbar: React.FC<GigToolbarProps> = ({
  transposeAmount,
  gigChordScale,
  onTranspose,
  onLyricsSizeChange,
  onChordScaleChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        transform: isOpen ? 'translateY(0)' : `translateY(${CLOSED_OFFSET}px)`,
        transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)',
        willChange: 'transform',
      }}
    >
      {/* Content bar — Figma #348:55: 402x82, #B8A99F, radius 20 20 0 0, boxShadow */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: `${CONTENT_H}px`,
          background: '#B8A99F',
          borderRadius: '20px 20px 0 0',
          boxShadow: '0px -8px 9.9px 0px rgba(0,0,0,0.12)',
        }}
      >
        {/* Toggle tap area — covers top 20px (pre-label zone), transparent */}
        <button
          onClick={() => setIsOpen(v => !v)}
          aria-label={isOpen ? 'Collapse toolbar' : 'Expand toolbar'}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '20px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', zIndex: 1 }}
        />
        {/* Figma frame: percentages below resolve against this 402px block, centered on wide screens */}
        <div style={{ position: 'relative', width: '100%', maxWidth: `${FRAME_W}px`, height: '100%', margin: '0 auto' }}>
        {/* Divider: x:22, y:4, w:358, border-top 5px solid #A2948A */}
        <div style={{ position: 'absolute', left: p(22), top: '4px', width: p(358), borderTop: '5px solid #A2948A' }} />

        {/* LYRICS SIZE ------------------------------------------------ */}
        {/* Label #348:48: x:30, y:17, w:90.1, h:19.55 — Kumbh Sans 400 8.5px UPPER 0.42em CENTER #271B13 */}
        <span style={{
          position: 'absolute', left: p(30), top: '17px', width: '90.1px', height: '19.55px',
          fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 400,
          fontSize: '8.5px', letterSpacing: '0.42em', textTransform: 'uppercase',
          color: t.darkBrown, textAlign: 'center', lineHeight: '19.55px', whiteSpace: 'nowrap',
        }}>
          lyrics size
        </span>
        {/* #348:49 "A" small: x:41, y:35, w:35.7, h:36.55 — Kumar One 400 14px UPPER CENTER */}
        <button
          onClick={() => onLyricsSizeChange(-1)}
          aria-label="Decrease lyrics size"
          style={{
            position: 'absolute', left: p(41), top: '35px', width: '35.7px', height: '36.55px',
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: '"Kumar One", sans-serif', fontWeight: 400, fontSize: '14px',
            textTransform: 'uppercase', color: t.darkBrown,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          A
        </button>
        {/* #348:54 "A" large: x:73, y:33, w:35.7, h:36.55 — Kumar One 400 24px UPPER CENTER */}
        <button
          onClick={() => onLyricsSizeChange(1)}
          aria-label="Increase lyrics size"
          style={{
            position: 'absolute', left: p(73), top: '33px', width: '35.7px', height: '36.55px',
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: '"Kumar One", sans-serif', fontWeight: 400, fontSize: '24px',
            textTransform: 'uppercase', color: t.darkBrown,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          A
        </button>

        {/* TRANSPOSE --------------------------------------------------- */}
        {/* Label #315:406: x:156, y:17, w:90.1, h:19.55 — same style */}
        <span style={{
          position: 'absolute', left: p(156), top: '17px', width: '90.1px', height: '19.55px',
          fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 400,
          fontSize: '8.5px', letterSpacing: '0.42em', textTransform: 'uppercase',
          color: t.darkBrown, textAlign: 'center', lineHeight: '19.55px', whiteSpace: 'nowrap',
        }}>
          transpose
        </span>
        {/* #315:408 minus-square: x:157.05, y:33.58, 34x34 — stroke:#271B13 (toolbar SVG) */}
        <button
          onClick={() => onTranspose(-1)}
          aria-label="Transpose down"
          style={{ position: 'absolute', left: p(157.05), top: '33.58px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src="/figma-assets/icons/gig-toolbar/minus-square-toolbar.svg" width="34" height="34" alt="-" style={{ display: 'block' }} />
        </button>
        {/* #315:407 value: x:183.2, y:31.6, w:35.7, h:36.55 — Kumbh Sans 400 17px UPPER CENTER */}
        <span style={{
          position: 'absolute', left: p(183.2), top: '31.6px', width: '35.7px', height: '36.55px',
          fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 400,
          fontSize: '17px', textTransform: 'uppercase',
          color: t.darkBrown, textAlign: 'center', lineHeight: '36.55px', whiteSpace: 'nowrap',
        }}>
          {transposeAmount}
        </span>
        {/* #315:409 plus-square: x:211.05, y:33.58, 34x34 — stroke:#271B13 (toolbar SVG) */}
        <button
          onClick={() => onTranspose(1)}
          aria-label="Transpose up"
          style={{ position: 'absolute', left: p(211.05), top: '33.58px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src="/figma-assets/icons/gig-toolbar/plus-square-toolbar.svg" width="34" height="34" alt="+" style={{ display: 'block' }} />
        </button>

        {/* CHORD SIZE -------------------------------------------------- */}
        {/* Label #348:35: x:273, y:17, w:90.1, h:19.55 — same style */}
        <span style={{
          position: 'absolute', left: p(273), top: '17px', width: '90.1px', height: '19.55px',
          fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 400,
          fontSize: '8.5px', letterSpacing: '0.42em', textTransform: 'uppercase',
          color: t.darkBrown, textAlign: 'center', lineHeight: '19.55px', whiteSpace: 'nowrap',
        }}>
          chord size
        </span>
        {/* #348:46 arrow-down: x:275, y:35, 30x30, rotate 180 */}
        <button
          onClick={() => onChordScaleChange(-0.05)}
          aria-label="Decrease chord size"
          style={{ position: 'absolute', left: p(275), top: '35px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src="/figma-assets/icons/gig-toolbar/arrow-up-circle.svg" width="30" height="30" alt="-" style={{ display: 'block', transform: 'rotate(180deg)' }} />
        </button>
        {/* #348:36 value: x:301, y:31.6, w:35.7, h:36.55 — Kumbh Sans 400 10px UPPER CENTER */}
        <span style={{
          position: 'absolute', left: p(301), top: '31.6px', width: '35.7px', height: '36.55px',
          fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 400,
          fontSize: '10px', textTransform: 'uppercase',
          color: t.darkBrown, textAlign: 'center', lineHeight: '36.55px', whiteSpace: 'nowrap',
        }}>
          {Math.round(gigChordScale * 100)}%
        </span>
        {/* #348:43 arrow-up: x:333, y:35, 30x30 */}
        <button
          onClick={() => onChordScaleChange(0.05)}
          aria-label="Increase chord size"
          style={{ position: 'absolute', left: p(333), top: '35px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src="/figma-assets/icons/gig-toolbar/arrow-up-circle.svg" width="30" height="30" alt="+" style={{ display: 'block' }} />
        </button>
        </div>
      </div>
    </div>
  );
};

export default GigToolbar;
